import {
  FC,
  PropsWithChildren,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { Snackbar, Alert, Button } from "@mui/material";
import variables from "styles/partials/_variables.module.scss";
import Success from "components/icons/Success";
import Warning from "components/icons/Warning";
import Error from "components/icons/Error";
import Info from "components/icons/Info";
import { ToastVariant } from "constants/ToastVariant";
import UndoIcon from "@mui/icons-material/Undo";
import { addMembersToFilterRoleByIDs } from "store/filterRole/thunks";
import { useAppDispatch } from "utils/hooks/storeHooks";

type ToastProps = {
  message: string;
  key?: string;
  open: boolean;
  undoChanges?: number[]; // to undo the member role change given the provided id values (role and member) in the array
  variant?: string;
  onClose: (event?: SyntheticEvent<Event, Event>) => void;
};
const Toast: FC<PropsWithChildren<ToastProps>> = ({
  message,
  key,
  open,
  undoChanges,
  variant,
  onClose,
}: PropsWithChildren<ToastProps>) => {
  const dispatch = useAppDispatch();
  const [showUndoBtn, setShowUndoBtn] = useState<boolean>(false);
  let icon;
  switch (variant) {
    case ToastVariant.Success:
      icon = <Success color={variables.accentGreen1}></Success>;
      break;
    case ToastVariant.Warning:
      icon = <Warning color={variables.accentYellow}></Warning>;
      break;
    case ToastVariant.Error:
      icon = <Error color={variables.accentRed}></Error>;
      break;
    case ToastVariant.Info:
      icon = <Info color={variables.primaryHoverBlue1}></Info>;
      break;
    default:
      icon = <Success color={variables.accentGreen1}></Success>;
      break;
  }

  // Toggle the state that allows to display the undo button according to the values provided in the undoChange array
  useEffect(() => {
    setShowUndoBtn(undoChanges?.length === 2);
  }, [undoChanges?.length]);

  const handleUndoChange = (currentRoleId: number, userId: number) => {
    dispatch(addMembersToFilterRoleByIDs(currentRoleId, [userId]));
    onClose();
    setShowUndoBtn(false);
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      autoHideDuration={2500}
      key={key}
      open={open}
      onClose={() => onClose()}
    >
      <Alert
        action={
          showUndoBtn &&
          undoChanges && (
            <Button
              color="inherit"
              endIcon={<UndoIcon />}
              size="small"
              sx={{ alignSelf: "center" }}
              onClick={() => handleUndoChange(undoChanges[0], undoChanges[1])}
            />
          )
        }
        icon={icon}
        sx={{ width: "100%" }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default Toast;
